import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Volume2, VolumeX } from 'lucide-react';
import { API_BASE } from '../services/api/core';
import './enter.css';

const FORMULAS = ['C₁₀H₁₅N', 'CH₃', '99.1% PURE', 'HEISENBERG'];
const MIN_CHARS = 8;

// Keyboard layout matching a real Dell laptop
const KEYBOARD_ROWS = [
  ['`', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '=', '⌫'],
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', '[', ']', '\\'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', ';', "'", 'ENTER'],
  ['⇧', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', ',', '.', '/'],
];

const BiohazardSVG = React.memo(({ className }) => (
  <svg className={className} viewBox="0 0 64 64" fill="#000000" aria-hidden="true">
    <circle cx="32" cy="32" r="6" />
    <path d="M32 12c-8 0-15 5-18 12 3-2 7-3 11-3 2-5 5-9 7-9s5 4 7 9c4 0 8 1 11 3-3-7-10-12-18-12z" />
    <path d="M14 38c-2 7 1 14 7 18 0-4 2-7 5-10-5-2-9-6-10-11-2 1-2 2-2 3z" />
    <path d="M50 38c0-1 0-2-2-3-1 5-5 9-10 11 3 3 5 6 5 10 6-4 9-11 7-18z" />
  </svg>
));

BiohazardSVG.displayName = 'BiohazardSVG';

const isProductionDomain =
  typeof window !== 'undefined' && window.location.hostname === 'yobitch.fun';

export default function EnterPage() {
  const [showSuccess, setShowSuccess] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState(0);
  const [password, setPassword] = useState('');
  const [canSlide, setCanSlide] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [laptopOpen, setLaptopOpen] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [activeKey, setActiveKey] = useState(null);
  const [capsOn, setCapsOn] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isPoweredOff, setIsPoweredOff] = useState(true); // Start powered off
  const [isBooting, setIsBooting] = useState(false);
  const [isShuttingDown, setIsShuttingDown] = useState(false);
  const [isPlayingStartup, setIsPlayingStartup] = useState(false);
  const [bootLines, setBootLines] = useState([]);
  const [currentTypingLine, setCurrentTypingLine] = useState('');
  const videoRef = useRef(null);
  const startupVideoRef = useRef(null);
  const navigate = useNavigate();
  const swipeStartRef = useRef(null);
  const [lidDragAngle, setLidDragAngle] = useState(null);

  const LID_OPEN_DEG = -5;
  const LID_CLOSED_DEG = -175;
  const LID_RANGE = LID_OPEN_DEG - LID_CLOSED_DEG;
  const DRAG_PIXELS = 120;

  const getSwipePoint = useCallback(e => {
    if (e.touches && e.touches.length) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    return { x: e.clientX, y: e.clientY };
  }, []);

  const getSwipeEndPoint = useCallback(e => {
    if (e.changedTouches && e.changedTouches.length) {
      return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
    }
    return { x: e.clientX, y: e.clientY };
  }, []);

  const getCurrentY = useCallback(e => {
    if (e.touches && e.touches.length) return e.touches[0].clientY;
    return e.clientY;
  }, []);

  const angleFromDrag = useCallback((startY, currentY, closing) => {
    const dy = currentY - startY;
    const progress = closing
      ? Math.min(dy / DRAG_PIXELS, 1)
      : Math.min((startY - currentY) / DRAG_PIXELS, 1);
    const t = Math.max(0, Math.min(1, progress));
    const angle = closing ? LID_OPEN_DEG - t * LID_RANGE : LID_CLOSED_DEG + t * LID_RANGE;
    return Math.max(LID_CLOSED_DEG, Math.min(LID_OPEN_DEG, angle));
  }, []);

  const handleLidMove = useCallback(
    e => {
      if (e.cancelable && e.type === 'touchmove') e.preventDefault();
      const drag = swipeStartRef.current;
      if (!drag) return;
      const y = getCurrentY(e);
      const angle = angleFromDrag(drag.startY, y, drag.closing);
      setLidDragAngle(angle);
    },
    [getCurrentY, angleFromDrag]
  );

  const cleanupLidDrag = useCallback(() => {
    setLidDragAngle(null);
    swipeStartRef.current = null;
    document.removeEventListener('touchmove', handleLidMove);
    window.removeEventListener('mousemove', handleLidMove);
  }, [handleLidMove]);

  const handleBezelSwipeEnd = useCallback(
    e => {
      const drag = swipeStartRef.current;
      if (!drag) return;
      const end = getSwipeEndPoint(e);
      const angle = angleFromDrag(drag.startY, end.y, drag.closing);
      setLaptopOpen(angle > (LID_OPEN_DEG + LID_CLOSED_DEG) / 2);
      cleanupLidDrag();
      document.removeEventListener('touchend', handleBezelSwipeEnd);
      document.removeEventListener('touchcancel', handleBezelSwipeEnd);
      window.removeEventListener('mouseup', handleBezelSwipeEnd);
    },
    [getSwipeEndPoint, angleFromDrag, cleanupLidDrag]
  );

  const handleBezelSwipeStart = useCallback(
    e => {
      if (!laptopOpen) return;
      const tag = e.target?.closest?.('button, a, input, [role="button"]');
      if (tag && tag !== e.currentTarget) return;
      if (e.type === 'touchstart') e.preventDefault();
      const pt = getSwipePoint(e);
      swipeStartRef.current = { startY: pt.y, closing: true };
      setLidDragAngle(LID_OPEN_DEG);
      document.addEventListener('touchmove', handleLidMove, { passive: false });
      document.addEventListener('touchend', handleBezelSwipeEnd, { once: true });
      document.addEventListener('touchcancel', handleBezelSwipeEnd, { once: true });
      window.addEventListener('mousemove', handleLidMove);
      window.addEventListener('mouseup', handleBezelSwipeEnd, { once: true });
    },
    [laptopOpen, getSwipePoint, handleLidMove, handleBezelSwipeEnd]
  );

  const handleClosedLidSwipeEnd = useCallback(
    e => {
      const drag = swipeStartRef.current;
      if (!drag) return;
      const end = getSwipeEndPoint(e);
      const angle = angleFromDrag(drag.startY, end.y, drag.closing);
      setLaptopOpen(angle > (LID_OPEN_DEG + LID_CLOSED_DEG) / 2);
      cleanupLidDrag();
      document.removeEventListener('touchend', handleClosedLidSwipeEnd);
      document.removeEventListener('touchcancel', handleClosedLidSwipeEnd);
      window.removeEventListener('mouseup', handleClosedLidSwipeEnd);
    },
    [getSwipeEndPoint, angleFromDrag, cleanupLidDrag]
  );

  const handleClosedLidSwipeStart = useCallback(
    e => {
      if (laptopOpen) return;
      if (e.type === 'touchstart') e.preventDefault();
      const pt = getSwipePoint(e);
      swipeStartRef.current = { startY: pt.y, closing: false };
      setLidDragAngle(LID_CLOSED_DEG);
      document.addEventListener('touchmove', handleLidMove, { passive: false });
      document.addEventListener('touchend', handleClosedLidSwipeEnd, { once: true });
      document.addEventListener('touchcancel', handleClosedLidSwipeEnd, { once: true });
      window.addEventListener('mousemove', handleLidMove);
      window.addEventListener('mouseup', handleClosedLidSwipeEnd, { once: true });
    },
    [laptopOpen, getSwipePoint, handleLidMove, handleClosedLidSwipeEnd]
  );

  // Boot messages - degen crypto breaking bad style
  const BOOT_MESSAGES = [
    'BIOS v4.20.69 - HEISENBERG LABS',
    'Initializing METH-OS...',
    'Loading crystal_pure.dll... 99.1%',
    'Checking DEX connections... CONNECTED',
    'Solana RPC: TURBO MODE ENGAGED',
    'Jito bundles: ARMED AND READY',
    'Scanning for snipers... REKT',
    'MEV protection: MAXIMUM',
    'Wallet aging: 420 DAYS',
    'Rugging prevention: ENABLED',
    'Loading pump.fun SDK...',
    '> "I am the one who launches" - W.W.',
    '',
    'SYSTEM READY. SAY MY NAME.',
  ];

  // Single source of truth: isMuted. toggleMute only updates state; sync effect handles refs.
  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  // Keep all video elements in sync with mute state (runs on every isMuted change)
  useEffect(() => {
    const muted = isMuted;
    const mainVideo = videoRef.current;
    const startupVideo = startupVideoRef.current;
    if (mainVideo) mainVideo.muted = muted;
    if (startupVideo) startupVideo.muted = muted;
  }, [isMuted]);

  // Handle Windows 95 startup video ended – main video respects current mute state
  const handleStartupVideoEnded = useCallback(() => {
    setIsPlayingStartup(false);
    setIsPoweredOff(false);
    const video = videoRef.current;
    if (video) {
      video.muted = isMuted;
      video.play().catch(() => {});
    }
    setTimeout(() => {
      if (!showLoginDialog && !showSuccess) {
        setShowNotification(true);
      }
    }, 1500);
  }, [isMuted, showLoginDialog, showSuccess]);

  // Reusable audio context for typing sounds
  const audioContextRef = useRef(null);

  // Play mechanical keyboard typing sound effect
  const playTypeSound = useCallback(() => {
    if (isMuted) return;
    try {
      // Reuse audio context to avoid creating too many
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const audioCtx = audioContextRef.current;

      // Resume if suspended (browser autoplay policy)
      if (audioCtx.state === 'suspended') {
        audioCtx.resume();
      }

      const now = audioCtx.currentTime;

      // Create noise for the mechanical click
      const bufferSize = audioCtx.sampleRate * 0.08; // 80ms of noise
      const noiseBuffer = audioCtx.createBuffer(1, bufferSize, audioCtx.sampleRate);
      const output = noiseBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        output[i] = Math.random() * 2 - 1;
      }

      const noise = audioCtx.createBufferSource();
      noise.buffer = noiseBuffer;

      // Filter to shape the click sound
      const filter = audioCtx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 2000 + Math.random() * 1500; // Vary the tone
      filter.Q.value = 1.5;

      // Second filter for more mechanical character
      const hipass = audioCtx.createBiquadFilter();
      hipass.type = 'highpass';
      hipass.frequency.value = 500;

      // Envelope for sharp attack
      const gainNode = audioCtx.createGain();
      gainNode.gain.setValueAtTime(0.15, now);
      gainNode.gain.exponentialRampToValueAtTime(0.08, now + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.06);

      // Connect the chain
      noise.connect(filter);
      filter.connect(hipass);
      hipass.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      noise.start(now);
      noise.stop(now + 0.08);
    } catch (e) {
      // Audio not supported, ignore
    }
  }, [isMuted]);

  // Type out a line character by character with organic timing
  const typeLineOrganic = useCallback(
    (line, onComplete) => {
      if (!line) {
        onComplete();
        return;
      }
      let charIndex = 0;
      setCurrentTypingLine('');

      const typeChar = () => {
        if (charIndex < line.length) {
          setCurrentTypingLine(line.substring(0, charIndex + 1));
          charIndex++;
          // Play typing sound
          playTypeSound();
          // Organic timing: random delay between 30-80ms, slower on punctuation
          const char = line[charIndex - 1];
          let delay = 30 + Math.random() * 50;
          if (char === '.' || char === ',' || char === ':') delay += 100;
          if (char === ' ') delay += 20;
          setTimeout(typeChar, delay);
        } else {
          setCurrentTypingLine('');
          onComplete();
        }
      };
      typeChar();
    },
    [playTypeSound]
  );

  // Power button handler
  const handlePowerButton = useCallback(() => {
    if (isBooting || isShuttingDown) return;

    if (!isPoweredOff) {
      // Power OFF - Windows 95 shutdown sequence
      setIsShuttingDown(true);
      setShowNotification(false);
      setShowLoginDialog(false);
      const video = videoRef.current;
      if (video) video.pause();

      // After shutdown animation, go to powered off state
      setTimeout(() => {
        setIsShuttingDown(false);
        setIsPoweredOff(true);
      }, 3000);
    } else {
      // Power ON - start boot sequence with organic typing
      setIsBooting(true);
      setBootLines([]);
      setCurrentTypingLine('');

      let lineIndex = 0;
      const typeNextLine = () => {
        if (lineIndex < BOOT_MESSAGES.length) {
          const line = BOOT_MESSAGES[lineIndex];
          lineIndex++;

          if (!line) {
            // Empty line - just add it and continue
            setBootLines(prev => [...prev, '']);
            setTimeout(typeNextLine, 300);
          } else {
            typeLineOrganic(line, () => {
              setBootLines(prev => [...prev, line]);
              // Random delay between lines for organic feel
              const delay = 200 + Math.random() * 400;
              setTimeout(typeNextLine, delay);
            });
          }
        } else {
          // Boot complete - play Windows 95 startup video
          setTimeout(() => {
            setIsBooting(false);
            setIsPlayingStartup(true);
            // Startup video will trigger onEnded to complete the boot
          }, 1000);
        }
      };

      // Start typing after brief delay
      setTimeout(typeNextLine, 500);
    }
  }, [
    isPoweredOff,
    isBooting,
    isShuttingDown,
    isMuted,
    showLoginDialog,
    showSuccess,
    typeLineOrganic,
  ]);

  // Safety: if startup video never ends (load error, autoplay blocked), complete boot after 8s
  useEffect(() => {
    if (!isPlayingStartup) return;
    const fallback = setTimeout(handleStartupVideoEnded, 8000);
    return () => clearTimeout(fallback);
  }, [isPlayingStartup, handleStartupVideoEnded]);

  // Show messenger-style notification shortly after boot completes (not just laptop open)
  useEffect(() => {
    if (!laptopOpen || showLoginDialog || showSuccess || isPoweredOff) return;
    const t = setTimeout(() => setShowNotification(true), 1500);
    return () => clearTimeout(t);
  }, [laptopOpen, showLoginDialog, showSuccess, isPoweredOff]);

  // Video only plays after boot - don't autoplay on mount
  // The boot sequence handler will start the video when ready

  // Check if password meets minimum length
  useEffect(() => {
    setCanSlide(password.length >= MIN_CHARS);
  }, [password]);

  const handleUnlock = useCallback(async () => {
    const trimmedPassword = password.trim();
    if (!trimmedPassword || verifying) return;
    setVerifying(true);
    try {
      const res = await fetch(`${API_BASE}/api/enter/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: trimmedPassword }),
      });
      const data = res.ok ? await res.json().catch(() => ({})) : {};
      if (res.ok && data.success) {
        if (typeof sessionStorage !== 'undefined') sessionStorage.setItem('enter_ok', '1');
        setShowSuccess(true);
        setTimeout(() => {
          setShowLoadingModal(true);
          setLoadingPhase(1);
          setTimeout(() => setLoadingPhase(2), 600);
          setTimeout(() => setLoadingPhase(3), 1400);
          setTimeout(() => setLoadingPhase(4), 2200);
          const targetPath = isProductionDomain ? '/app/connect' : '/connect';
          setTimeout(() => navigate(targetPath), 3800);
        }, 500);
      } else {
        setShowErrorModal(true);
        setTimeout(() => {
          setShowErrorModal(false);
          setPassword('');
        }, 2500);
      }
    } catch {
      setShowErrorModal(true);
      setTimeout(() => {
        setShowErrorModal(false);
        setPassword('');
      }, 2500);
    } finally {
      setVerifying(false);
    }
  }, [password, navigate, verifying]);

  const handleKeyPress = useCallback(
    key => {
      if (showSuccess) return;
      setActiveKey(key);
      setTimeout(() => setActiveKey(null), 100);

      // Play mechanical keyboard sound
      playTypeSound();

      if (key === '⌫') {
        setPassword(prev => prev.slice(0, -1));
      } else if (key === 'SPACE') {
        setPassword(prev => prev + ' ');
      } else if (key === 'ENTER') {
        if (password.length >= MIN_CHARS) {
          handleUnlock();
        }
      } else if (key === '⇧') {
        setCapsOn(prev => !prev);
      } else {
        const char = /[A-Za-z]/.test(key) ? (capsOn ? key : key.toLowerCase()) : key;
        setPassword(prev => prev + char);
      }
    },
    [password, showSuccess, handleUnlock, playTypeSound, capsOn]
  );

  const handleOkClick = useCallback(() => {
    if (password.length >= MIN_CHARS) {
      handleUnlock();
    }
  }, [password, handleUnlock]);

  const smokeElements = useMemo(
    () => Array.from({ length: 6 }, (_, i) => <div key={i} className="smoke" />),
    []
  );

  const toxicSmokeElements = useMemo(
    () => Array.from({ length: 2 }, (_, i) => <div key={i} className="toxic-smoke" />),
    []
  );

  const crystalElements = useMemo(() => {
    // Pre-computed deterministic values
    const crystals = [];
    for (let i = 0; i < 15; i++) {
      const size = 30 + ((i * 7) % 40);
      const left = (i * 17) % 100;
      const delay = (i * 0.4) % 6;
      const duration = 6 + (i % 4);
      crystals.push(
        <img
          key={i}
          src="/images/ice-button.webp"
          alt="Crystal button"
          className="meth-crystal"
          style={{
            left: `${left}%`,
            animationDelay: `${delay}s`,
            animationDuration: `${duration}s`,
            width: `${size}px`,
            height: 'auto',
          }}
          onError={e => {
            e.target.onerror = null;
            e.target.src =
              'data:image/svg+xml,' +
              encodeURIComponent(
                '<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60" viewBox="0 0 60 60"><polygon points="30,2 58,20 58,45 30,58 2,45 2,20" fill="#7dd3fc" stroke="#fff" stroke-width="1" opacity="0.8"/><polygon points="30,8 50,22 50,42 30,52 10,42 10,22" fill="#a5f3fc" opacity="0.6"/></svg>'
              );
          }}
        />
      );
    }
    return crystals;
  }, []);

  const fallingPantsElements = useMemo(() => {
    // Pre-computed deterministic values
    const pants = [];
    for (let i = 0; i < 8; i++) {
      const size = 40 + ((i * 8) % 60);
      const startX = (i * 13) % 100;
      const swayAmount = 30 + ((i * 7) % 50);
      const rotation = (i * 45) % 360;
      const rotationSpeed = (i % 2 === 0 ? 1 : -1) * (180 + ((i * 40) % 360));
      const delay = (i * 1.25) % 10;
      const duration = 8 + (i % 6);
      pants.push(
        <img
          key={i}
          src="/images/icon-pants.webp"
          alt=""
          className="falling-pants"
          style={{
            left: `${startX}%`,
            animationDelay: `${delay}s`,
            animationDuration: `${duration}s`,
            width: `${size}px`,
            height: 'auto',
            '--sway-amount': `${swayAmount}px`,
            '--start-rotation': `${rotation}deg`,
            '--rotation-speed': `${rotationSpeed}deg`,
          }}
        />
      );
    }
    return pants;
  }, []);

  const homePath = isProductionDomain ? '/app/about' : '/about';
  return (
    <div className="landing-container" role="main" aria-label="Enter">
      <Link to={homePath} className="landing-about-link" aria-label="Home">
        Home
      </Link>
      <div className="hazmat-stripe top" aria-hidden="true" />

      <div className="smoke-layer" aria-hidden="true">
        {smokeElements}
      </div>

      <div className="smoke-layer" aria-hidden="true">
        {toxicSmokeElements}
      </div>

      <div className="crystals" aria-hidden="true">
        {crystalElements}
      </div>

      <div className="falling-pants-container" aria-hidden="true">
        {fallingPantsElements}
      </div>

      <BiohazardSVG className="biohazard left" />
      <BiohazardSVG className="biohazard right" />

      <div className="vignette" aria-hidden="true" />
      <div className="scanlines" aria-hidden="true" />

      <div className="title-container"></div>

      {/* Windows balloon notification - OUTSIDE laptop for click handling */}
      {laptopOpen && !showSuccess && showNotification && !showLoginDialog && (
        <div
          role="button"
          tabIndex={0}
          className="win-balloon-notification"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setShowNotification(false);
            setShowLoginDialog(true);
          }}
          onMouseDown={e => {
            e.preventDefault();
            e.stopPropagation();
            setShowNotification(false);
            setShowLoginDialog(true);
          }}
          onTouchStart={e => {
            e.preventDefault();
            e.stopPropagation();
            setShowNotification(false);
            setShowLoginDialog(true);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowNotification(false);
              setShowLoginDialog(true);
            }
          }}
          aria-label="Network login required - click to enter password"
        >
          <div className="win-balloon-icon">🔐</div>
          <div className="win-balloon-content">
            <div className="win-balloon-title">Network Login Required</div>
            <div className="win-balloon-text">Click here to enter your password.</div>
          </div>
          <div className="win-balloon-close">×</div>
        </div>
      )}

      {/* Fallback: open login when boot done and balloon not clicked */}
      {laptopOpen &&
        !showSuccess &&
        !showLoginDialog &&
        !isPoweredOff &&
        !isBooting &&
        !isPlayingStartup && (
          <button
            type="button"
            onClick={() => setShowLoginDialog(true)}
            style={{
              position: 'fixed',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 9998,
              fontSize: 12,
              color: 'var(--bb-yellow)',
              background: 'rgba(0,0,0,0.8)',
              padding: '10px 16px',
              borderRadius: 6,
              border: '1px solid rgba(244,208,63,0.5)',
              cursor: 'pointer',
              pointerEvents: 'auto',
            }}
          >
            Enter password to continue
          </button>
        )}

      {/* Windows 95 Login Dialog - OUTSIDE laptop for click handling */}
      {laptopOpen && !showSuccess && showLoginDialog && (
        <div className="win95-login-dialog">
          <div className="win95-titlebar">
            <span className="win95-title">🔑 Enter Network Password</span>
            <div className="win95-titlebar-buttons">
              <button type="button" className="win95-btn-minimize" aria-label="Minimize">
                _
              </button>
              <button
                type="button"
                className="win95-btn-close"
                aria-label="Close"
                onClick={() => setShowLoginDialog(false)}
              >
                ×
              </button>
            </div>
          </div>
          <div className="win95-content">
            <div className="win95-icon-section">
              <div className="win95-key-icon">🔐</div>
            </div>
            <div className="win95-form-section">
              <p className="win95-message">Enter password for LAB access:</p>
              <div className="win95-field">
                <label className="win95-label">User name:</label>
                <div className="win95-input-display">HEISENBERG</div>
              </div>
              <div className="win95-field">
                <label className="win95-label">Password:</label>
                <div className="win95-input-display password">
                  {password ? '●'.repeat(password.length) : ''}
                  <span className="win95-cursor">|</span>
                </div>
              </div>
            </div>
          </div>
          <div className="win95-buttons">
            <button
              type="button"
              className={`win95-btn ${canSlide ? 'active' : 'disabled'}`}
              onClick={handleOkClick}
              disabled={!canSlide}
            >
              OK
            </button>
            <button type="button" className="win95-btn" onClick={() => setShowLoginDialog(false)}>
              Cancel
            </button>
          </div>
          <div className="win95-status">
            {canSlide
              ? '✓ Ready - Press OK or Enter'
              : `Type password using keyboard below (${password.length}/${MIN_CHARS})`}
          </div>
        </div>
      )}

      {/* Success message - OUTSIDE laptop for visibility */}
      {laptopOpen && showSuccess && (
        <div className="win95-success-dialog">
          <div className="win95-titlebar success">
            <span className="win95-title">✓ Access Granted</span>
          </div>
          <div className="win95-success-content">
            <div className="win95-success-icon">✅</div>
            <p className="win95-success-text">YOU&apos;RE GODDAMN RIGHT</p>
          </div>
        </div>
      )}

      <div className={`lab-monitor dell-laptop ${laptopOpen ? 'open' : 'closed'}`}>
        <div
          className={`laptop-lid ${lidDragAngle !== null ? 'lid-dragging' : ''}`}
          style={lidDragAngle !== null ? { transform: `rotateX(${lidDragAngle}deg)` } : undefined}
        >
          <div
            className="laptop-bezel"
            role="button"
            tabIndex={laptopOpen ? 0 : -1}
            aria-label={laptopOpen ? 'Swipe down to close laptop' : undefined}
            onTouchStart={laptopOpen ? handleBezelSwipeStart : undefined}
            onMouseDown={laptopOpen ? handleBezelSwipeStart : undefined}
          >
            <div className="bezel-top">
              <div className="webcam-dot"></div>
            </div>
            <div className={`laptop-screen ${isPoweredOff || isShuttingDown ? 'screen-off' : ''}`}>
              {!isPoweredOff && !isShuttingDown && (
                <div className="screen-header win95-titlebar">
                  <div className="win95-titlebar-buttons">
                    <button type="button" className="win95-btn-minimize" aria-label="Minimize">
                      _
                    </button>
                    <button type="button" className="win95-btn-maximize" aria-label="Maximize">
                      □
                    </button>
                    <button type="button" className="win95-btn-close" aria-label="Close">
                      ×
                    </button>
                  </div>
                </div>
              )}
              <div className="video-wrapper">
                {/* Windows 95 Shutdown screen */}
                {isShuttingDown && (
                  <div className="shutdown-screen">
                    <div className="shutdown-content">
                      <div className="shutdown-logo">
                        <img
                          src="/images/windows95.png"
                          alt="Windows 95"
                          className="win95-logo-img"
                        />
                      </div>
                      <div className="shutdown-text">It&apos;s now safe to turn off</div>
                      <div className="shutdown-subtext">your computer.</div>
                    </div>
                  </div>
                )}
                {/* Power off / Boot screen */}
                {(isPoweredOff || isBooting) && !isShuttingDown && !isPlayingStartup && (
                  <div className={`boot-screen ${isBooting ? 'booting' : 'off'}`}>
                    {isPoweredOff && !isBooting && (
                      <div className="power-off-text">CLICK POWER TO RESTART</div>
                    )}
                    {isBooting && (
                      <div className="boot-terminal">
                        {bootLines.map((line, i) => (
                          <div key={i} className="boot-line">
                            {line}
                          </div>
                        ))}
                        {currentTypingLine && (
                          <div className="boot-line typing">
                            {currentTypingLine}
                            <span className="boot-cursor">_</span>
                          </div>
                        )}
                        {!currentTypingLine && <span className="boot-cursor">_</span>}
                      </div>
                    )}
                  </div>
                )}
                {/* Windows 95 Startup Video */}
                {isPlayingStartup && (
                  <div className="startup-video-container">
                    <video
                      ref={startupVideoRef}
                      className="startup-video"
                      autoPlay
                      muted={isMuted}
                      playsInline
                      onEnded={handleStartupVideoEnded}
                      onError={() => handleStartupVideoEnded()}
                      onLoadedData={() => {
                        const v = startupVideoRef.current;
                        if (v) {
                          v.muted = isMuted;
                          v.play().catch(() => handleStartupVideoEnded());
                        }
                      }}
                      onCanPlay={() => {
                        const v = startupVideoRef.current;
                        if (v) v.play().catch(() => handleStartupVideoEnded());
                      }}
                    >
                      <source src="/windows_95_startup.mp4" type="video/mp4" />
                    </video>
                  </div>
                )}
                {/* eslint-disable react/no-unknown-property */}
                {!videoError ? (
                  <video
                    ref={videoRef}
                    className="video"
                    loop
                    muted={isMuted}
                    playsInline
                    webkit-playsinline="true"
                    x5-playsinline="true"
                    preload="auto"
                    poster="/images/heisenberg-bg.jpg"
                    aria-label="Background video"
                    onError={() => setVideoError(true)}
                    style={{
                      opacity:
                        isPoweredOff || isBooting || isShuttingDown || isPlayingStartup ? 0 : 1,
                    }}
                  >
                    <source src="/landingpagefly-optimized.mp4" type="video/mp4" />
                    <source src="/yobitch.mp4" type="video/mp4" />
                  </video>
                ) : (
                  <img
                    src="/images/heisenberg-bg.jpg"
                    alt="Lab"
                    className="video"
                    style={{
                      objectFit: 'cover',
                      opacity:
                        isPoweredOff || isBooting || isShuttingDown || isPlayingStartup ? 0 : 1,
                    }}
                  />
                )}
                {!isPoweredOff && !isShuttingDown && (
                  <>
                    <div className="video-overlay"></div>
                    <div className="video-scanline"></div>
                    <div className="video-corner-logo" aria-hidden="true">
                      <img src="/images/logo.png" alt="YoBitch" className="video-corner-logo-img" />
                    </div>
                  </>
                )}
              </div>

              {/* Windows XP Taskbar - hidden when powered off */}
              {!isPoweredOff && !isShuttingDown && (
                <div className="winxp-taskbar">
                  <button type="button" className="winxp-start-btn">
                    <img
                      src="/images/windows95.png"
                      alt="Windows"
                      className="winxp-start-logo-img"
                    />
                    <span className="winxp-start-text">start</span>
                  </button>
                  <div className="winxp-quicklaunch">
                    <span className="winxp-ql-icon">🌐</span>
                    <span className="winxp-ql-icon">📧</span>
                    <span className="winxp-ql-icon">📁</span>
                  </div>
                  <div className="winxp-tasks">
                    <div className="winxp-task active">
                      <span className="winxp-task-icon">🔐</span>
                      <span className="winxp-task-text">Network Login</span>
                    </div>
                  </div>
                  <div className="winxp-systray">
                    <span className="winxp-tray-icon">🔊</span>
                    <span className="winxp-tray-icon">🛡️</span>
                    <span className="winxp-clock">12:03 PM</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="lid-edge"></div>
          {/* Closed lid cover - swipe up to open (like real device) */}
          <div
            className="closed-lid-cover"
            role="button"
            tabIndex={0}
            aria-label="Swipe up to open laptop"
            onTouchStart={handleClosedLidSwipeStart}
            onTouchEnd={handleClosedLidSwipeEnd}
            onMouseDown={handleClosedLidSwipeStart}
            onKeyDown={e => e.key === 'Enter' && !laptopOpen && setLaptopOpen(true)}
          >
            <img src="/images/dell-logo.png" alt="Dell" className="closed-lid-logo" />
            <img
              src="/images/sticker1.png"
              alt=""
              className="closed-lid-sticker-img closed-lid-sticker-1"
            />
            <img
              src="/images/sticker2.png"
              alt=""
              className="closed-lid-sticker-img closed-lid-sticker-2"
            />
            <img
              src="/images/sticker3.png"
              alt=""
              className="closed-lid-sticker-img closed-lid-sticker-3"
            />
            <img
              src="/images/sticker4.png"
              alt=""
              className="closed-lid-sticker-img closed-lid-sticker-4"
            />
            <img
              src="/images/sticker5.png"
              alt=""
              className="closed-lid-sticker-img closed-lid-sticker-5"
            />
            <img
              src="/images/sticker6.png"
              alt=""
              className="closed-lid-sticker-img closed-lid-sticker-6"
            />
            <img
              src="/images/sticker7.png"
              alt=""
              className="closed-lid-sticker-img closed-lid-sticker-7"
            />
            <img
              src="/images/sticker8.png"
              alt=""
              className="closed-lid-sticker-img closed-lid-sticker-8"
            />
          </div>
        </div>
        <div className="laptop-keyboard">
          {/* Stickers on palm rest */}
          <div className="laptop-stickers">
            <div className="sticker sticker-intel">
              <img src="/images/intel.png" alt="Intel" className="sticker-intel-img" />
            </div>
            <div className="sticker sticker-win" aria-label="Windows">
              <img src="/images/windows95sticker.png" alt="Windows" className="sticker-win-img" />
            </div>
          </div>
          <div className="keyboard-top">
            <div className="keyboard-top-left">
              <div className="speaker-grille left"></div>
            </div>
            <div className="keyboard-top-center">
              <img src="/images/dell-logo.png" alt="Dell" className="keyboard-dell-logo" />
            </div>
            <div className="keyboard-top-right">
              <div className="speaker-grille right"></div>
              <button
                type="button"
                className={`mute-button ${isMuted ? 'muted' : ''}`}
                onClick={toggleMute}
                aria-label={isMuted ? 'Unmute audio' : 'Mute audio'}
              >
                <span className="mute-button-icon" aria-hidden="true">
                  {isMuted ? (
                    <VolumeX size={12} strokeWidth={2.5} />
                  ) : (
                    <Volume2 size={12} strokeWidth={2.5} />
                  )}
                </span>
              </button>
              <button
                type="button"
                className={`power-button ${isPoweredOff ? 'off' : 'on'} ${isBooting ? 'booting' : ''} ${isShuttingDown ? 'shutting-down' : ''}`}
                onClick={handlePowerButton}
                aria-label={isPoweredOff ? 'Power on' : 'Power off'}
              >
                <span className="power-icon">⏻</span>
              </button>
            </div>
          </div>
          <div className={`keyboard-keys ${showLoginDialog && !showSuccess ? 'active' : ''}`}>
            {KEYBOARD_ROWS.map((row, rowIndex) => (
              <div key={rowIndex} className="key-row">
                {row.map(key => {
                  const isEnter = key === 'ENTER';
                  const isShift = key === '⇧';
                  return (
                    <div
                      key={isEnter ? 'ENTER' : key}
                      className={`key ${key === '⌫' ? 'backspace' : ''} ${isEnter ? 'enter' : ''} ${isShift ? 'shift' : ''} ${isShift && capsOn ? 'caps-on' : ''} ${activeKey === (isEnter ? 'ENTER' : key) ? 'pressed' : ''} ${showLoginDialog && !showSuccess ? 'clickable' : ''}`}
                      onClick={() =>
                        showLoginDialog && !showSuccess && handleKeyPress(isEnter ? 'ENTER' : key)
                      }
                    >
                      <span className="key-label">
                        {isEnter ? (
                          <>
                            <span>Enter</span>
                            <span className="key-enter-icon" aria-hidden="true">
                              ↵
                            </span>
                          </>
                        ) : isShift ? (
                          '⇧ Shift'
                        ) : (
                          key
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
            <div className="key-row bottom">
              <div className="key ctrl">
                <span className="key-label">Ctrl</span>
              </div>
              <div className="key fn">
                <span className="key-label">Fn</span>
              </div>
              <div className="key win">
                <span className="key-label">⊞</span>
              </div>
              <div className="key alt">
                <span className="key-label">Alt</span>
              </div>
              <div
                className={`key spacebar ${activeKey === 'SPACE' ? 'pressed' : ''} ${showLoginDialog && !showSuccess ? 'clickable' : ''}`}
                onClick={() => showLoginDialog && !showSuccess && handleKeyPress('SPACE')}
              ></div>
              <div className="key alt">
                <span className="key-label">Alt</span>
              </div>
            </div>
          </div>
          <div className="trackpad-area">
            <div className="touchpad"></div>
            <div className="trackpad-buttons">
              <div className="trackpad-btn left"></div>
              <div className="trackpad-btn right"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Modal - Yellow to White Transition */}
      {showLoadingModal && (
        <div className={`loading-modal-overlay phase-${loadingPhase}`}>
          <div className={`loading-top-text ${loadingPhase >= 3 ? 'visible' : ''}`}>
            <span>⚠ CAUTION ⚠</span>
          </div>

          <div className={`loading-center ${loadingPhase >= 2 ? 'visible' : ''}`}>
            <img
              src="/images/lets-cook.png"
              alt="Let's Cook"
              className="heisenberg-logo"
              onError={e => {
                e.target.onerror = null;
                e.target.src = '/images/logo.png';
              }}
            />
          </div>

          <div className={`loading-bottom-text ${loadingPhase >= 3 ? 'visible' : ''}`}>
            <span>99.1% PURE</span>
          </div>

          <div className={`loading-progress-bar ${loadingPhase >= 4 ? 'visible' : ''}`}>
            <div className="progress-fill"></div>
          </div>
        </div>
      )}

      {/* Error Modal - Red Background */}
      {showErrorModal && (
        <div className="error-modal-overlay">
          <div className="error-content">
            <img
              src="/images/lets-cook.png"
              alt="Wrong Answer"
              className="error-heisenberg"
              onError={e => {
                e.target.onerror = null;
                e.target.src = '/images/logo.png';
              }}
            />
            <div className="error-text">WRONG ANSWER</div>
            <div className="error-subtext">
              You clearly don&apos;t know who you&apos;re talking to
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
